import React, { useState } from 'react';
import * as customerApi from 'src/api/customer';
import { Customer } from 'src/stores/CustomerStore';
import { ToastStoreObject, ToastType } from 'src/stores/ToastStore';
import { parseError } from 'src/utils';
import ConfirmationModal from '../ConfirmationModal';
import Flex from '../Flex';
import { Label } from '../Input';
import LoadingIcon from '../LoadingIcon';
import ModalFooter from '../ModalFooter';
import ModalHeader from '../ModalHeader';
import SideModal from '../SideModal';
import Toggle from '../Toggle';
import styles from './styles.module.scss';

interface EditableOrganization {
    orgId: number;
    orgNm: string;
    orgNmIntrnl: string;
    captureAiV2Enabled?: boolean;
}

interface EditOrganizationEntitlementsModalProps {
    customerId: Customer['customerId'];
    organization: EditableOrganization;
    onClose?: Function;
    onSave?: Function;
}

function EditOrganizationEntitlementsModal(props: EditOrganizationEntitlementsModalProps) {
    const initialCaptureAiV2Enabled = props.organization.captureAiV2Enabled === true;
    const [captureAiV2Enabled, setCaptureAiV2Enabled] = useState(initialCaptureAiV2Enabled);
    const [isLoading, setIsLoading] = useState(false);
    const [confirmDisable, setConfirmDisable] = useState(false);

    async function save() {
        setIsLoading(true);
        try {
            await customerApi.updateCustomerOrgCaptureAiV2Entitlement(
                props.customerId,
                props.organization.orgId,
                captureAiV2Enabled,
            );
            if (props.onSave) await props.onSave();
            if (props.onClose) props.onClose();
            ToastStoreObject.show(
                `Capture AI v2 has been ${captureAiV2Enabled ? 'enabled' : 'disabled'} for ${
                    props.organization.orgNm
                }`,
                ToastType.Success,
            );
        } catch (error) {
            ToastStoreObject.show(parseError(error), ToastType.Error);
        } finally {
            setIsLoading(false);
            setConfirmDisable(false);
        }
    }

    function handleSave() {
        if (initialCaptureAiV2Enabled && !captureAiV2Enabled) {
            setConfirmDisable(true);
            return;
        }
        return save();
    }

    return (
        <>
            <SideModal isOpen={true} onClose={props.onClose}>
                <ModalHeader title="Edit Organization" onClose={props.onClose} />
                {isLoading ? (
                    <div className={styles.loadingContentWrap}>
                        <LoadingIcon />
                    </div>
                ) : (
                    <div className={styles.contentWrap}>
                        <div className={styles.organizationName}>{props.organization.orgNm}</div>
                        <div className={styles.organizationIdentifier}>{props.organization.orgNmIntrnl}</div>
                        <Flex className={styles.optionRow} align="center">
                            <Toggle
                                ariaLabel="Enable Capture AI v2 Document Types"
                                className={styles.orgOptionToggle}
                                toggled={captureAiV2Enabled}
                                onChange={(enabled: boolean) => setCaptureAiV2Enabled(enabled)}
                            />
                            <div className={styles.optionDescription}>
                                <Label text="Capture AI v2 (Document Types)" />
                                <small>
                                    Enables document type configuration for this organization. Facilities are activated
                                    separately in Facility Settings.
                                </small>
                            </div>
                        </Flex>
                        {initialCaptureAiV2Enabled && !captureAiV2Enabled ? (
                            <div className={styles.warning}>
                                Existing document type configuration will be preserved, but Document Types and v2 runtime
                                access will be unavailable. Facilities currently using v2 must be changed first.
                            </div>
                        ) : null}
                    </div>
                )}
                <ModalFooter
                    primaryText="Save"
                    primaryClick={handleSave}
                    secondaryClick={() => props.onClose?.()}
                    isLoading={isLoading}
                    primaryProps={{ disabled: captureAiV2Enabled === initialCaptureAiV2Enabled }}
                />
            </SideModal>
            {confirmDisable ? (
                <ConfirmationModal
                    headerTitle="Disable Capture AI v2?"
                    title="Document Types and Capture AI v2 runtime access will be unavailable for this organization. Existing configuration will be preserved."
                    confirmButtonText="Disable Capture AI v2"
                    onConfirm={save}
                    onCancel={() => setConfirmDisable(false)}
                    isLoading={isLoading}
                />
            ) : null}
        </>
    );
}

export default EditOrganizationEntitlementsModal;
